import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { DollarSign, Download, Users, TrendingUp, Plus, Trash2, Trophy } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const CHART_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#06b6d4', '#8b5cf6'];

export default function CommissionsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [summary, setSummary] = useState(null);
  const [config, setConfig] = useState(null);
  const [tierDialog, setTierDialog] = useState(false);
  const [tierForm, setTierForm] = useState({ name: '', min_sales: 0, commission_pct: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/commissions/summary'),
      api.get('/commissions/config').catch(() => ({ data: null })),
    ]).then(([s, c]) => {
      setSummary(s.data);
      setConfig(c.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const downloadPdf = async () => {
    try {
      const r = await api.get('/commissions/report-pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'reporte_comisiones.pdf';
      a.click();
      toast.success('Reporte descargado');
    } catch { toast.error('Error al generar reporte'); }
  };

  const saveTier = async () => {
    if (!tierForm.name) return;
    const tiers = [...(config?.tiers || []), tierForm];
    try {
      const r = await api.put('/commissions/tiers', { tiers });
      setConfig(c => ({ ...c, tiers: r.data.tiers }));
      setTierDialog(false);
      setTierForm({ name: '', min_sales: 0, commission_pct: 0 });
      toast.success('Tier agregado');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const deleteTier = async (idx) => {
    const tiers = (config?.tiers || []).filter((_, i) => i !== idx);
    try {
      const r = await api.put('/commissions/tiers', { tiers });
      setConfig(c => ({ ...c, tiers: r.data.tiers }));
    } catch { toast.error('Error'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">Cargando...</div>;

  if (summary && summary.vertical_ok === false) {
    return (
      <div className="p-6" data-testid="commissions-wrong-vertical">
        <h2 className="text-2xl font-bold text-white tracking-tight mb-2">Comisiones</h2>
        <p className="text-sm text-muted-foreground">{summary.message || 'Este esquema es para inmobiliarias y franquicias.'}</p>
      </div>
    );
  }

  const byAgent = summary?.by_agent || [];
  const details = summary?.details || [];
  const tiers = config?.tiers || [];

  return (
    <div className="space-y-6" data-testid="commissions-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white tracking-tight">Comisiones</h2>
          <p className="text-sm text-muted-foreground">El % del inmueble manda. Configuración es el respaldo. Al cerrar una oportunidad se genera el pendiente de pago.</p>
        </div>
        {isAdmin && (
          <Button onClick={downloadPdf} variant="outline" className="border-white/10 text-xs" data-testid="download-commission-pdf">
            <Download className="w-4 h-4 mr-2" /> Descargar PDF
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card/50 border-white/5"><CardContent className="p-4">
          <div className="w-9 h-9 rounded-lg bg-indigo-500/10 flex items-center justify-center mb-2"><TrendingUp className="w-5 h-5 text-indigo-500" /></div>
          <p className="text-2xl font-bold text-white">${(summary?.total_projected || 0).toLocaleString()}</p>
          <p className="text-[10px] text-muted-foreground uppercase">Proyectado Total</p>
        </CardContent></Card>
        <Card className="bg-card/50 border-white/5"><CardContent className="p-4">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-2"><DollarSign className="w-5 h-5 text-emerald-500" /></div>
          <p className="text-2xl font-bold text-emerald-400">${(summary?.total_won || 0).toLocaleString()}</p>
          <p className="text-[10px] text-muted-foreground uppercase">Ganado (Cerrado)</p>
        </CardContent></Card>
        <Card className="bg-card/50 border-white/5"><CardContent className="p-4">
          <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center mb-2"><DollarSign className="w-5 h-5 text-amber-500" /></div>
          <p className="text-2xl font-bold text-amber-400">${(summary?.total_pending || 0).toLocaleString()}</p>
          <p className="text-[10px] text-muted-foreground uppercase">Pendiente de Pago</p>
        </CardContent></Card>
        <Card className="bg-card/50 border-white/5"><CardContent className="p-4">
          <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center mb-2"><Users className="w-5 h-5 text-blue-500" /></div>
          <p className="text-2xl font-bold text-white">{byAgent.length}</p>
          <p className="text-[10px] text-muted-foreground uppercase">Agentes con Comision</p>
        </CardContent></Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Chart: By Agent */}
        {byAgent.length > 0 && (
          <Card className="bg-card/50 border-white/5">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-white">Comisiones por Agente</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={byAgent}>
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} />
                  <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: '8px', fontSize: '11px' }} />
                  <Bar dataKey="projected" name="Proyectado" radius={[4, 4, 0, 0]}>
                    {byAgent.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Performance Tiers */}
        <Card className="bg-card/50 border-white/5">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-sm text-white flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-400" /> Tiers de Rendimiento</CardTitle>
              <CardDescription className="text-xs">Bonos por nivel de ventas alcanzado</CardDescription>
            </div>
            {isAdmin && <Button variant="ghost" size="sm" className="text-xs text-indigo-400" onClick={() => setTierDialog(true)} data-testid="add-tier-btn"><Plus className="w-3 h-3 mr-1" />Agregar</Button>}
          </CardHeader>
          <CardContent className="space-y-2">
            {tiers.length === 0 && <p className="text-[10px] text-muted-foreground text-center py-4">Sin tiers configurados. Ve a Configuracion &gt; Comisiones para agregar.</p>}
            {tiers.map((t, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5" data-testid={`tier-${i}`}>
                <div>
                  <p className="text-xs font-medium text-white">{t.name}</p>
                  <p className="text-[10px] text-muted-foreground">Min. {t.min_sales} ventas → {t.commission_pct}% comision</p>
                </div>
                {isAdmin && <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive" onClick={() => deleteTier(i)}><Trash2 className="w-3 h-3" /></Button>}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {(summary?.records || []).length > 0 && (
        <Card className="bg-card/50 border-white/5" data-testid="commission-records">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-white">Pendientes y pagos</CardTitle>
            <CardDescription className="text-xs">Se genera al marcar la oportunidad como ganada. Solo admin marca pagado.</CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-left min-w-[520px]">
              <thead>
                <tr className="bg-black/20">
                  <th className="p-2.5 text-[10px] text-muted-foreground">Monto agente</th>
                  <th className="p-2.5 text-[10px] text-muted-foreground">%</th>
                  <th className="p-2.5 text-[10px] text-muted-foreground">Origen</th>
                  <th className="p-2.5 text-[10px] text-muted-foreground">Estado</th>
                  {isAdmin && <th className="p-2.5 text-[10px] text-muted-foreground" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {(summary.records || []).map((r) => (
                  <tr key={r.id} data-testid={`commission-record-${r.id}`}>
                    <td className="p-2.5 text-xs text-emerald-400">${Number(r.amount || 0).toLocaleString()}</td>
                    <td className="p-2.5 text-[10px] text-muted-foreground">{r.commission_pct}%</td>
                    <td className="p-2.5 text-[10px] text-muted-foreground">{r.pct_source === 'property' ? 'Inmueble' : (r.pct_source || 'config')}</td>
                    <td className="p-2.5">
                      <Badge variant="outline" className={`text-[9px] ${r.status === 'paid' ? 'text-emerald-400 border-emerald-400/20' : 'text-amber-400 border-amber-400/20'}`}>
                        {r.status === 'paid' ? 'Pagado' : 'Pendiente'}
                      </Badge>
                    </td>
                    {isAdmin && (
                      <td className="p-2.5 text-right">
                        {r.status !== 'paid' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-[10px] border-emerald-500/30 text-emerald-300"
                            data-testid={`pay-commission-${r.id}`}
                            onClick={async () => {
                              try {
                                await api.post(`/commissions/records/${r.id}/pay`);
                                const s = await api.get('/commissions/summary');
                                setSummary(s.data);
                                toast.success('Marcado como pagado');
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'No se pudo pagar');
                              }
                            }}
                          >
                            Marcar pagado
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Detail Table */}
      {details.length > 0 && (
        <Card className="bg-card/50 border-white/5">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-white">Detalle de Comisiones por Oportunidad</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[700px]">
              <thead>
                <tr className="bg-black/20">
                  <th className="p-2.5 text-[10px] font-semibold text-muted-foreground border-b border-white/5">Oportunidad</th>
                  <th className="p-2.5 text-[10px] font-semibold text-muted-foreground border-b border-white/5">Propiedad</th>
                  <th className="p-2.5 text-[10px] font-semibold text-muted-foreground border-b border-white/5">Agente</th>
                  <th className="p-2.5 text-[10px] font-semibold text-muted-foreground border-b border-white/5 text-right">Valor</th>
                  <th className="p-2.5 text-[10px] font-semibold text-muted-foreground border-b border-white/5 text-right">%</th>
                  <th className="p-2.5 text-[10px] font-semibold text-muted-foreground border-b border-white/5 text-right">Comision</th>
                  <th className="p-2.5 text-[10px] font-semibold text-muted-foreground border-b border-white/5 text-center">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {details.slice(0, 30).map((d, i) => (
                  <tr key={i}>
                    <td className="p-2.5 text-xs text-white">{d.opp_title || '-'}</td>
                    <td className="p-2.5 text-xs text-muted-foreground">{d.property_title || '-'}</td>
                    <td className="p-2.5 text-xs text-muted-foreground">{d.agent_name}</td>
                    <td className="p-2.5 text-xs text-white text-right">${d.value?.toLocaleString()}</td>
                    <td className="p-2.5 text-[10px] text-right text-muted-foreground" title={d.pct_source === 'property' ? 'Del inmueble' : 'De configuración'}>
                      {d.commission_pct}%{d.pct_source === 'property' ? ' · inmueble' : ''}{d.tier_name ? ` · ${d.tier_name}` : ''}
                    </td>
                    <td className="p-2.5 text-xs text-emerald-400 text-right">${d.total_commission?.toLocaleString()}</td>
                    <td className="p-2.5 text-center">
                      <Badge variant="outline" className={`text-[9px] ${d.is_won ? 'text-emerald-400 border-emerald-400/20' : 'text-amber-400 border-amber-400/20'}`}>
                        {d.is_won ? 'Cerrado' : 'Proyectado'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Add Tier Dialog */}
      <Dialog open={tierDialog} onOpenChange={setTierDialog}>
        <DialogContent className="sm:max-w-sm" data-testid="tier-dialog">
          <DialogHeader><DialogTitle>Agregar Tier de Rendimiento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label className="text-xs">Nombre del Tier</Label>
              <Input data-testid="tier-name" value={tierForm.name} onChange={e => setTierForm({...tierForm, name: e.target.value})} placeholder="Ej: Gold, Platinum" className="bg-black/20 border-white/10 text-xs" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Min. Ventas</Label>
                <Input data-testid="tier-min-sales" type="number" value={tierForm.min_sales} onChange={e => setTierForm({...tierForm, min_sales: parseInt(e.target.value) || 0})} className="bg-black/20 border-white/10 text-xs" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">% Comision Extra</Label>
                <Input data-testid="tier-pct" type="number" step="0.5" value={tierForm.commission_pct} onChange={e => setTierForm({...tierForm, commission_pct: parseFloat(e.target.value) || 0})} className="bg-black/20 border-white/10 text-xs" />
              </div>
            </div>
            <Button onClick={saveTier} className="w-full bg-amber-600 hover:bg-amber-500 text-white text-xs" data-testid="save-tier-btn">
              <Plus className="w-3 h-3 mr-1" /> Agregar Tier
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
